import React from 'react';
import { FormProvider } from 'react-hook-form';
import { Save } from 'lucide-react';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { GreenBeanOriginSection } from './components/GreenBeanOriginSection';
import { GreenBeanProcessingSection } from './components/GreenBeanProcessingSection';
import { GreenBeanQualitySection } from './components/GreenBeanQualitySection';
import { CreateInventoryHeader } from './components/CreateInventoryHeader';
import { InventoryBasicInfoSection } from './components/InventoryBasicInfoSection';
import { InventoryStockCostSection } from './components/InventoryStockCostSection';
import { useCreateInventoryPageState } from '../../hooks/inventory-hooks/useCreateInventoryPageState';
import { useLanguage } from '../../features/language/useLanguage';

export const CreateInventoryPage: React.FC = () => {
  const { t } = useLanguage();
  const {
    navigate,
    isEditMode,
    isLoadingItem,
    methods,
    register,
    handleSubmit,
    setValue,
    control,
    errors,
    isSubmitting,
    isGreenBean,
    isWeightBased,
    unit,
    toggleUnit,
    uniqueOptions,
    onSubmit,
    standardCost,
  } = useCreateInventoryPageState();

  if (isEditMode && isLoadingItem) {
    return <LoadingSpinner text={t('inventory.modal.loadingItemDetails')} />;
  }

  return (
    <FormProvider {...methods}>
      <div className="space-y-6" data-test-id="inventory-create-page">
        <CreateInventoryHeader isEditMode={isEditMode} onBack={() => navigate('/inventory')} />

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8 pb-10">
          <InventoryBasicInfoSection register={register} errors={errors} />

          <InventoryStockCostSection
            isWeightBased={isWeightBased}
            unit={unit}
            onToggleUnit={toggleUnit}
            control={control}
            register={register}
            standardCost={standardCost}
            setValue={setValue}
          />

          {isGreenBean && (
            <div className="bg-white shadow rounded-lg p-6 space-y-8" data-test-id="green-bean-profile-section">
              <div className="flex items-center justify-between border-b pb-4">
                <h2 className="text-lg font-medium text-gray-900">{t('greenBeanProfile.title')}</h2>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('inventory.createPage.profileCode')}</label>
                    <input
                      type="text"
                      {...register('green_bean_profile.profile_code')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
                      data-test-id="green-bean-profile-code"
                      placeholder={t('inventory.modal.values.optional')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('inventory.createPage.profileName')}</label>
                    <input
                      type="text"
                      {...register('green_bean_profile.profile_name')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
                      data-test-id="green-bean-profile-name"
                      placeholder={t('inventory.modal.values.optional')}
                    />
                  </div>
                </div>
              </div>

              <GreenBeanOriginSection uniqueOptions={uniqueOptions} disabled={false} />
              <GreenBeanProcessingSection uniqueOptions={uniqueOptions} disabled={false} />
              <GreenBeanQualitySection disabled={false} />
            </div>
          )}

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/inventory')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {t('auth.common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              data-test-id="inventory-create-submit"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSubmitting ? t('forms.actions.saving') : isEditMode ? t('inventory.modal.actions.updateItem') : t('inventory.modal.actions.createItem')}
            </button>
          </div>
        </form>
      </div>
    </FormProvider>
  );
};
